import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpDown } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import FilterPopover from "../components/FilterPopover";
import { useSettings, formatMoney } from "../lib/settings";

const COLS = [
  { key: "name", label: "Company" },
  { key: "domain", label: "Domain" },
  { key: "industry", label: "Industry" },
  { key: "employees", label: "Employees" },
  { key: "contact_count", label: "Contacts" },
  { key: "active_deal_count", label: "Active Deals" },
  { key: "pipeline_value", label: "Pipeline Value" },
  { key: "created_at", label: "Created" },
];

export default function Companies() {
  const { currency } = useSettings();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [filterState, setFilterState] = useState({ industry: [], deals: [] });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
    } catch { toast.error("Failed to load companies"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = companies;
    if (filterState.industry.length > 0) {
      const set = new Set(filterState.industry);
      list = list.filter((c) => set.has(c.industry || "—"));
    }
    if (filterState.deals.length > 0) {
      list = list.filter((c) => {
        const n = c.active_deal_count || 0;
        return filterState.deals.some((bucket) => {
          if (bucket === "0") return n === 0;
          if (bucket === "1-3") return n >= 1 && n <= 3;
          if (bucket === "4+") return n >= 4;
          return false;
        });
      });
    }
    const sorted = [...list].sort((a, b) => {
      const va = a[sortKey] ?? "";
      const vb = b[sortKey] ?? "";
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sortDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
    return sorted;
  }, [companies, sortKey, sortDir, filterState]);

  const industryOptions = useMemo(() => {
    const set = new Set();
    companies.forEach((c) => { if (c.industry) set.add(c.industry); });
    return Array.from(set).sort().map((n) => ({ value: n, label: n }));
  }, [companies]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  return (
    <div data-testid="companies-page">
      <PageHeader title="Companies" subtitle="every business in your pipeline" />

      <div className="px-8 pt-4 pb-2 flex items-center gap-2">
        <FilterPopover
          testid="companies-filter"
          matchCount={filtered.length}
          value={filterState}
          onChange={setFilterState}
          groups={[
            { key: "industry", title: "Industry", options: industryOptions },
            {
              key: "deals",
              title: "Active Deals",
              options: [
                { value: "0",   label: "No active deals" },
                { value: "1-3", label: "1–3" },
                { value: "4+",  label: "4 or more" },
              ],
            },
          ]}
        />
      </div>

      <div className="p-8 pt-4">
        {loading ? (
          <div className="text-mute mono text-xs">loading…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state" data-testid="companies-empty">
            No companies yet. They are created automatically when a Lead is converted.
          </div>
        ) : (
          <div className="pg-card" style={{ overflow: "hidden" }}>
            <table className="pg-table">
              <thead>
                <tr>
                  {COLS.map(c => (
                    <th key={c.key} style={{ cursor: "pointer" }} onClick={() => toggleSort(c.key)} data-testid={`col-${c.key}`}>
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        <ArrowUpDown size={11} style={{ opacity: sortKey === c.key ? 1 : 0.35 }} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/companies/${c.id}`)} data-testid={`company-row-${c.id}`}>
                    <td style={{ fontWeight: 600, color: "var(--pg-text)" }}>{c.name}</td>
                    <td className="mono text-xs">{c.domain || "—"}</td>
                    <td>{c.industry || "—"}</td>
                    <td>{c.employees || "—"}</td>
                    <td className="mono">{c.contact_count}</td>
                    <td className="mono">{c.active_deal_count}</td>
                    <td className="mono" style={{ color: "var(--pg-accent-deep)", fontWeight: 600 }}>
                      {formatMoney(c.pipeline_value, currency.symbol)}
                    </td>
                    <td className="mono text-xs">{(c.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
